import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Users,
  Settings,
  Calendar,
  FolderOpen,
  Plus,
  Zap,
  Building2,
  BarChart3,
  DollarSign,
  Globe,
  ChevronDown,
  Menu,
  Sparkles,
  Castle,
  Ship,
  FileText,
  ClipboardList,
} from "lucide-react";
import CreateTripModal from "../agent/trips/CreateTripModal";
import CreateClientModal from "../agent/clients/CreateClientModal";
import { useUser } from "../../context/UserContext";
import { useIsRouteAvailable } from "../../hooks/usePhaseAccess";
import { isParkProAdmin } from "../../utils/accessControl";
import "../../styles/modern-design-system.css";

/**
 * SidebarV2 — Unified navigation sidebar.
 *
 * Structure:
 *  My Workspace  — visible to all authenticated users (agentUser, agentAdmin, parkProAdmin)
 *  My Agency     — visible only to agency-plan account owners (canSeeAgencyAdmin)
 *
 * No mode toggle. Both sections are always visible to eligible users.
 * ParkPro admins see all destinations and bypass phase gates.
 */
const SidebarV2 = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedTripManagement, setExpandedTripManagement] = useState(() => {
    try { return localStorage.getItem('sidebar_trips_expanded') !== 'false'; } catch { return true; }
  });
  const [expandedAgency, setExpandedAgency] = useState(() => {
    try { return localStorage.getItem('sidebar_agency_expanded') !== 'false'; } catch { return true; }
  });
  const [showCreateTripModal, setShowCreateTripModal] = useState(false);
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const { user, canSeeAgencyAdmin } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const drawerRef = useRef(null);

  // ParkPro admins see all four destinations; all other users see Disney World + coming-soon Disneyland
  const showAllDestinations = isParkProAdmin(user?.role);

  // Phase-based availability checks (hooks must be at top level)
  const isCrmClientsAvailable      = useIsRouteAvailable('/agent/crm/clients');
  const isCrmTripsAvailable         = useIsRouteAvailable('/agent/crm/trips');
  const isAdminDashboardAvailable   = useIsRouteAvailable('/agent/admin/dashboard');
  const isAdminAgentsAvailable      = useIsRouteAvailable('/agent/admin/agents');
  const isAdminFinancialAvailable   = useIsRouteAvailable('/agent/admin/financial');
  const isAdminWhiteLabelingAvailable = useIsRouteAvailable('/agent/admin/white-labeling');
  const isAdminReportsAvailable     = useIsRouteAvailable('/agent/admin/reports');

  // Whether any agency admin section items are available (used to decide if section renders)
  const hasAnyAdminItems = isAdminDashboardAvailable || isAdminAgentsAvailable ||
    isAdminFinancialAvailable || isAdminWhiteLabelingAvailable || isAdminReportsAvailable;

  // Derive trip section expanded state: always open on trip pages, else use toggle
  const isOnTripPage =
    location.pathname.startsWith("/agent/crm/trips") ||
    location.pathname.startsWith("/agent/crm/disney-world") ||
    location.pathname.startsWith("/agent/crm/disneyland") ||
    location.pathname.startsWith("/agent/crm/universal-orlando") ||
    location.pathname.startsWith("/agent/crm/disney-cruise");
  const tripManagementExpanded = isOnTripPage || expandedTripManagement;

  const isActive = (path) => {
    if (path === '/agent/dashboard') return location.pathname === '/agent/dashboard';
    return location.pathname.startsWith(path);
  };

  // Focus trap and ESC close for mobile drawer
  useEffect(() => {
    if (!mobileOpen) return;
    drawerRef.current?.focus();
    const handleKeyDown = (e) => {
      if (e.key === "Escape") { setMobileOpen(false); return; }
      if (e.key === "Tab") {
        const focusable = drawerRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [mobileOpen]);

  const handleClientCreated = (newClient) => {
    window.dispatchEvent(new CustomEvent('clientCreated', { detail: newClient }));
    if (!location.pathname.startsWith('/agent/crm/clients')) {
      navigate('/agent/crm/clients');
    }
  };

  const navItemClass = (active) =>
    `flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-150 sm:px-4 sm:py-2.5 ${
      active
        ? "bg-blue-50 text-blue-700 border-l-[3px] border-blue-600"
        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
    }`;

  const sectionLabelClass = "px-3 sm:px-4 mb-1.5 text-xs font-semibold tracking-wider text-gray-400 uppercase select-none";

  const renderNav = (onItemClick) => (
    <div className="sidebar-inner flex flex-col h-full min-h-0">

      {/* ── HEADER: Quick Actions ─────────────────────────────────────── */}
      <div className="flex-shrink-0 px-3 pt-3 pb-3 border-b border-gray-200 sm:px-4 sm:pt-4 sm:pb-4">
        <div className="space-y-1.5">
          <button
            onClick={() => { setShowCreateTripModal(true); onItemClick?.(); }}
            className="flex items-center px-3 py-2 w-full text-sm font-medium text-blue-700 bg-blue-50 rounded-lg border border-blue-200 transition-colors hover:bg-blue-100 sm:px-4 sm:py-2.5"
          >
            <Plus className="mr-2 w-4 h-4 sm:mr-3" aria-hidden="true" />
            New Trip
          </button>
          <button
            onClick={() => { setShowCreateClientModal(true); onItemClick?.(); }}
            className="flex items-center px-3 py-2 w-full text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-200 transition-colors hover:bg-gray-50 sm:px-4 sm:py-2.5"
          >
            <Plus className="mr-2 w-4 h-4 sm:mr-3" aria-hidden="true" />
            Add Client
          </button>
        </div>
      </div>

      {/* ── SCROLLABLE NAV ────────────────────────────────────────────── */}
      <div className="sidebar-scroll overflow-y-auto flex-1 min-h-0 px-3 pt-3 pb-2 sm:px-4 sm:pt-4 sm:pb-3">

        {/* ── MY WORKSPACE ──────────────────────────────────────────── */}
        <div className="mb-4">
          <p className={sectionLabelClass}>My Workspace</p>

          {/* Dashboard */}
          <Link
            to="/agent/dashboard"
            className={navItemClass(isActive('/agent/dashboard'))}
            onClick={onItemClick}
          >
            <Home className="mr-2 w-4 h-4 sm:mr-3" aria-hidden="true" />
            Dashboard
          </Link>

          {/* Client Management */}
          {isCrmClientsAvailable && (
            <Link
              to="/agent/crm/clients"
              className={navItemClass(isActive('/agent/crm/clients'))}
              onClick={onItemClick}
            >
              <Users className="mr-2 w-4 h-4 sm:mr-3" aria-hidden="true" />
              Clients
            </Link>
          )}

          {/* Trip Management — collapsible */}
          <div className="mb-1">
            <button
              type="button"
              aria-expanded={tripManagementExpanded}
              className={navItemClass(
                isActive('/agent/crm/trips') ||
                isActive('/agent/crm/disney-world') ||
                isActive('/agent/crm/disneyland') ||
                isActive('/agent/crm/universal-orlando') ||
                isActive('/agent/crm/disney-cruise')
              ) + " w-full justify-between"}
              onClick={() => setExpandedTripManagement((prev) => { const next = !prev; try { localStorage.setItem('sidebar_trips_expanded', next); } catch {} return next; })}
            >
              <div className="flex items-center">
                <Calendar className="mr-2 w-4 h-4 sm:mr-3" aria-hidden="true" />
                Trip Management
              </div>
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${tripManagementExpanded ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
            </button>

            {tripManagementExpanded && (
              <div className="mt-0.5 ml-6 space-y-0.5">
                {isCrmTripsAvailable && (
                  <Link
                    to="/agent/crm/trips"
                    className={navItemClass(isActive('/agent/crm/trips'))}
                    onClick={onItemClick}
                  >
                    <ClipboardList className="mr-2 w-4 h-4 sm:mr-3" aria-hidden="true" />
                    All Trips
                  </Link>
                )}

                {/* Disney World — available to all users */}
                <Link
                  to="/agent/crm/disney-world"
                  className={navItemClass(isActive('/agent/crm/disney-world'))}
                  onClick={onItemClick}
                >
                  <Sparkles className="mr-2 w-4 h-4 sm:mr-3" aria-hidden="true" />
                  Disney World
                </Link>

                {showAllDestinations ? (
                  <>
                    <Link
                      to="/agent/crm/disneyland"
                      className={navItemClass(isActive('/agent/crm/disneyland'))}
                      onClick={onItemClick}
                    >
                      <Castle className="mr-2 w-4 h-4 sm:mr-3" aria-hidden="true" />
                      Disneyland
                    </Link>
                    <Link
                      to="/agent/crm/universal-orlando"
                      className={navItemClass(isActive('/agent/crm/universal-orlando'))}
                      onClick={onItemClick}
                    >
                      <Globe className="mr-2 w-4 h-4 sm:mr-3" aria-hidden="true" />
                      Universal Studios
                    </Link>
                    <Link
                      to="/agent/crm/disney-cruise"
                      className={navItemClass(isActive('/agent/crm/disney-cruise'))}
                      onClick={onItemClick}
                    >
                      <Ship className="mr-2 w-4 h-4 sm:mr-3" aria-hidden="true" />
                      Disney Cruise
                    </Link>
                  </>
                ) : (
                  /* Disneyland — coming soon for non-admin users */
                  <div
                    className="flex items-center px-3 py-2 text-sm font-medium text-gray-400 rounded-lg cursor-default sm:px-4 sm:py-2.5 overflow-hidden"
                    aria-disabled="true"
                    role="presentation"
                    title="Coming soon — we're expanding destination support"
                  >
                    <Castle className="flex-shrink-0 mr-2 w-4 h-4 sm:mr-3" aria-hidden="true" />
                    <span className="flex-1 min-w-0 truncate">Disneyland</span>
                    <span className="flex-shrink-0 ml-1.5 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-gray-500 bg-gray-100 border border-gray-200 rounded whitespace-nowrap">
                      Soon
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Wonder Vault */}
          <Link
            to="/agent/documents"
            className={navItemClass(isActive('/agent/documents'))}
            onClick={onItemClick}
          >
            <FolderOpen className="mr-2 w-4 h-4 sm:mr-3" aria-hidden="true" />
            Wonder Vault
          </Link>
        </div>

        {/* ── MY AGENCY (agency-plan owners only) ───────────────────── */}
        {canSeeAgencyAdmin && hasAnyAdminItems && (
          <div className="mt-2 pt-3 border-t border-gray-200">

            {/* Section header — collapsible toggle */}
            <button
              type="button"
              aria-expanded={expandedAgency}
              className="flex items-center justify-between w-full px-3 sm:px-4 mb-1.5 group"
              onClick={() => setExpandedAgency((prev) => { const next = !prev; try { localStorage.setItem('sidebar_agency_expanded', next); } catch {} return next; })}
            >
              <span className="text-xs font-semibold tracking-wider text-gray-400 uppercase select-none group-hover:text-gray-500 transition-colors">
                My Agency
              </span>
              <ChevronDown
                className={`w-3.5 h-3.5 text-gray-400 group-hover:text-gray-500 transition-all duration-200 ${expandedAgency ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
            </button>

            {expandedAgency && (
              <div className="space-y-0.5">
                {isAdminDashboardAvailable && (
                  <Link
                    to="/agent/admin/dashboard"
                    className={navItemClass(isActive('/agent/admin/dashboard'))}
                    onClick={onItemClick}
                  >
                    <BarChart3 className="mr-2 w-4 h-4 sm:mr-3" aria-hidden="true" />
                    Agency Overview
                  </Link>
                )}

                {isAdminAgentsAvailable && (
                  <Link
                    to="/agent/admin/agents"
                    className={navItemClass(isActive('/agent/admin/agents'))}
                    onClick={onItemClick}
                  >
                    <Users className="mr-2 w-4 h-4 sm:mr-3" aria-hidden="true" />
                    Agent Management
                  </Link>
                )}

                {isAdminFinancialAvailable && (
                  <Link
                    to="/agent/admin/financial"
                    className={navItemClass(isActive('/agent/admin/financial'))}
                    onClick={onItemClick}
                  >
                    <DollarSign className="mr-2 w-4 h-4 sm:mr-3" aria-hidden="true" />
                    Financials
                  </Link>
                )}

                {isAdminReportsAvailable && (
                  <Link
                    to="/agent/admin/reports"
                    className={navItemClass(isActive('/agent/admin/reports'))}
                    onClick={onItemClick}
                  >
                    <FileText className="mr-2 w-4 h-4 sm:mr-3" aria-hidden="true" />
                    Reports
                  </Link>
                )}

                {isAdminWhiteLabelingAvailable && (
                  <Link
                    to="/agent/admin/white-labeling"
                    className={navItemClass(isActive('/agent/admin/white-labeling'))}
                    onClick={onItemClick}
                  >
                    <Building2 className="mr-2 w-4 h-4 sm:mr-3" aria-hidden="true" />
                    White Labeling
                  </Link>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── FOOTER: Settings ──────────────────────────────────────────── */}
      <div className="flex-shrink-0 mt-auto px-3 py-2.5 border-t border-gray-300 sm:px-4 sm:py-3">
        <Link
          to="/agent/settings"
          className={navItemClass(isActive('/agent/settings'))}
          onClick={onItemClick}
        >
          <Settings className="mr-2 w-4 h-4 sm:mr-3" aria-hidden="true" />
          Settings
        </Link>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className="hidden sticky top-0 z-40 flex-col w-64 h-full min-h-0 bg-white border-r border-gray-200 shadow-sm md:flex"
        aria-label="Main navigation"
        style={{ overflow: 'hidden' }}
      >
        {renderNav()}
      </aside>

      {/* Mobile Toggle Button */}
      <button
        className="fixed top-3 left-3 z-50 p-2 bg-white rounded-lg border border-gray-200 shadow-lg md:hidden"
        onClick={() => setMobileOpen(true)}
        aria-label="Open navigation menu"
      >
        <Menu className="w-5 h-5 text-gray-600" aria-hidden="true" />
      </button>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div
          className="flex fixed inset-0 z-50 backdrop-blur-sm bg-black/60"
          aria-modal="true"
          role="dialog"
          aria-label="Navigation drawer"
        >
          <div
            ref={drawerRef}
            className="flex flex-col w-72 h-full min-h-0 bg-white shadow-2xl outline-none sm:w-80"
            tabIndex={-1}
          >
            {/* Mobile Header */}
            <div className="flex flex-shrink-0 justify-between items-center p-3 border-b border-gray-200 sm:p-4">
              <h3 className="text-lg font-semibold text-gray-900">Menu</h3>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 text-gray-400 rounded-lg hover:text-gray-600 hover:bg-gray-100"
                aria-label="Close navigation menu"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {renderNav(() => setMobileOpen(false))}
          </div>
        </div>
      )}

      {/* Modals */}
      <CreateTripModal
        isOpen={showCreateTripModal}
        onClose={() => setShowCreateTripModal(false)}
        onTripCreated={() => {}}
      />
      <CreateClientModal
        isOpen={showCreateClientModal}
        onClose={() => setShowCreateClientModal(false)}
        onClientCreated={handleClientCreated}
      />
    </>
  );
};

export default SidebarV2;
